import BestOfferPriceDetailsOptionsDetails from '@ui/Accommodation/Card/BestOffer/PriceDetails/OptionsDetails/component'
import accommodationCard, { initImagesSlider } from '@ui/Accommodation/Card/component'
import AccommodationDetailsConditions from '@ui/Accommodation/Details/Conditions/component'
import { createXhr as coreCommonCreateXhr, debounce, isDesktop } from 'assets/core/js/common'
import dataLayer from 'assets/core/js/module/dataLayer'
import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import { PartialsEvents } from 'assets/core/js/module/partialsManager'
import { initZoomMediasPanel } from 'assets/core/js/page/product/zoomMediasPanel'
import { openCalendar } from './calendar'
import { createSidePanel } from './side-panels/accommodation'
import type { SidePanelType } from '@ui/SidePanel/component'
import type Swiper from 'swiper'

const toggleZoomMediaPanel = (_card: HTMLElement, image: HTMLImageElement): void => {
  initZoomMediasPanel(image)
}

const openDetails = (card: HTMLElement): void => {
  const accommodationId = card.getAttribute('data-id')

  const detailsEl = card.parentNode?.querySelector<HTMLElement>(`.accommodation-details[data-id="${accommodationId}"]`)

  if (!detailsEl) {
    return
  }

  card.toggleAttribute('data-details-clicked')
  const hidden = detailsEl.toggleAttribute('hidden')

  detailsEl.querySelectorAll<HTMLElement>('.accommodation-details-conditions').forEach((el) => {
    AccommodationDetailsConditions(el)
  })

  // Make a read offer call when the details just opened.
  if (!hidden) {
    const readOfferUrl = detailsEl.getAttribute('data-read-offer-url')
    if (readOfferUrl) {
      const readOfferXhr = coreCommonCreateXhr('POST', readOfferUrl)
      // Deliberately ignore the response.
      readOfferXhr.send()
    }
  }
}

const openSidePanel = (card: HTMLElement): void => {
  let sidePanel: SidePanelType | undefined
  const sidePanelExists = ElementPropertiesManager.hasProperty(card, 'sidePanel')

  if (!sidePanelExists) {
    const sidePanelId = card.getAttribute('data-sidepanel-id') as string
    const sidePanelUrl = card.getAttribute('data-sidepanel-url') as string
    const sidePanelType = card.getAttribute('data-sidepanel-type') as 'accommodation' | 'pitch'
    const accommodationId = card.getAttribute('data-id') ?? ''
    sidePanel = createSidePanel(sidePanelId, sidePanelUrl, sidePanelType, accommodationId)

    ElementPropertiesManager.addProperty(card, 'sidePanel', sidePanel)
  }

  sidePanel = ElementPropertiesManager.getProperty<SidePanelType>(card, 'sidePanel')
  sidePanel?.open()
}

const initCards = (): void => {
  document.querySelectorAll<HTMLElement>('.accommodation-card').forEach((el) => {
    const cardPanelType = el.getAttribute('data-panel-type') as 'side-panel' | 'bottom-panel'

    accommodationCard(el, {
      onClickCard: (card) => {
        if (cardPanelType === 'side-panel') {
          openSidePanel(card)
        }

        if (cardPanelType === 'bottom-panel') {
          openDetails(card)
        }
      },
      onClickPropertiesViewMoreBtn: (card) => {
        if (cardPanelType === 'side-panel') {
          openSidePanel(card)
        }

        if (cardPanelType === 'bottom-panel') {
          openDetails(card)
        }
      },
      onClickImage: (card, image) => {
        if (cardPanelType === 'side-panel' && !isDesktop()) {
          openSidePanel(card)
        } else {
          toggleZoomMediaPanel(card, image)
        }
      },
      onClickDetailsBtn: (card) => {
        if (cardPanelType === 'side-panel') {
          openSidePanel(card)
        }

        if (cardPanelType === 'bottom-panel') {
          openDetails(card)
        }
      },
      onClickBestOfferBtn: (card, button) => {
        if (button.closest('form[name="checkOffer"]')) {
          return
        }

        if (!card.hasAttribute('data-checkin-date')) {
          openCalendar(button)
        }

        if (card.hasAttribute('data-checkin-date') && cardPanelType === 'side-panel') {
          openSidePanel(card)
        }
      },
      onClickChangeDateBtn: (_card, button) => {
        openCalendar(button)
      },
      onClickPlanBtn() {
        dataLayer.pushInLayer(window.coreGtmId, {
          event: dataLayer.events.ACCOMMODATION_PLAN_VIEW,
        })
      },
      enableImagesSlider: isDesktop(),
    })

    // Initialize the PriceDetails:OptionsDetails component if there is one.
    const bestOfferPriceDetailsOptionsDetailsElement = el.querySelector<HTMLElement>('.accommodation-card-best-offer-price-details-options-details')
    if (bestOfferPriceDetailsOptionsDetailsElement) {
      BestOfferPriceDetailsOptionsDetails(bestOfferPriceDetailsOptionsDetailsElement)
    }
  })

  const resizeFn = debounce(100, () => {
    document.querySelectorAll<HTMLElement>('.accommodation-card').forEach((el) => {
      const imagesSliderEl = el.querySelector<HTMLElement>('.accommodation-card__images .swiper')

      if (!imagesSliderEl) {
        return
      }

      const slider = ElementPropertiesManager.getProperty<Swiper>(imagesSliderEl, 'slider')

      if (isDesktop() && !slider) {
        initImagesSlider(el)
        window.removeEventListener('resize', resizeFn)
      }
    })
  })

  window.addEventListener('resize', resizeFn)
}

export default function (): void {
  const resultsEl = document.querySelector('[data-partial-id="results"]')

  if (!resultsEl) {
    return
  }

  resultsEl.addEventListener(PartialsEvents.POST_UPDATE, () => {
    initCards()
  })

  // don't init the cards if the page is in progress of updating the results list
  if (!resultsEl.hasAttribute('data-partial-status')) {
    initCards()
  }
}
